import { useEffect } from "react";
import { useI18n, SUPPORTED } from "@/i18n";

const ORIGIN = "https://realestatepro.be";

// Proper Open Graph locale codes (language_TERRITORY) per UI language.
const OG_LOCALE = { nl: "nl_BE", en: "en_GB", fr: "fr_BE", es: "es_ES" };

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, hreflang, href) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(sel);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * SPA-friendly multilingual SEO for public pages. Sets a localized <title>,
 * meta description, Open Graph text, a self-referencing canonical and hreflang
 * alternates for nl/en/fr/es (+ x-default). Every language canonicalizes to its
 * stable prefixed URL (/nl, /en, /fr, /es) because the unprefixed URL is
 * preference-aware: it renders the visitor's session rep_lang and defaults to
 * English — so it is the x-default target, never a language-specific one.
 * `page` selects the SEO copy namespace, `path` is the path suffix
 * ("" for home, "/terms" for legal, etc.).
 */
export function SeoHead({ page = "home", path = "" }) {
  const { lang, t } = useI18n();
  useEffect(() => {
    const title = t(`seo.${page}.title`);
    const desc = t(`seo.${page}.description`);
    document.title = title;
    upsertMeta("name", "description", desc);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:locale", OG_LOCALE[lang] || lang);

    upsertLink("canonical", null, `${ORIGIN}/${lang}${path}`);
    SUPPORTED.forEach((l) => {
      upsertLink("alternate", l, `${ORIGIN}/${l}${path}`);
    });
    // x-default = the unprefixed, preference-aware URL (saved language, else EN).
    upsertLink("alternate", "x-default", `${ORIGIN}${path || "/"}`);
  }, [lang, page, path, t]);
  return null;
}
